import { SERVICE_ADJUSTMENT_TYPES } from 'src/constants/cart';
import { BreakdownItem, Prepaid } from 'src/types/cart.types';
import { currencyFormatter } from 'src/utils/formatting';
import { TSimplifiedBreakdownItem } from './CartNavigationDisplay.types';

/** Subscription/member discount */
export const getMemberDiscount = (item = {} as BreakdownItem) => item.savings || 0;

/**
 * Calculates the bundle discount for a given item.
 *
 * This discount is referred to by many names, e.g. "automatic", "volume", and "bundle" discount.
 * It's applied when certain conditions are met, such as purchasing multiple services together.
 */
export const getBundleDiscount = (item = {} as BreakdownItem) => {
  if (!item.adjustments || !item.adjustments.length) return 0;
  const bundleDiscount = item.adjustments.find(adj => adj.name.trim().toLowerCase() === SERVICE_ADJUSTMENT_TYPES.AUTOMATIC_DISCOUNT);
  return bundleDiscount ? Math.abs(bundleDiscount.amount as number) : 0;
};

/** Calculates and formats price and discount information for a cart line item. */
export const getDiscountsTotal = (item = {} as BreakdownItem) => {
  // Currently the only line item discounts are for multiple bundles or membership
  const discounts = [getBundleDiscount(item), getMemberDiscount(item)];
  return discounts.reduce((acc, elem) => acc + elem, 0);
};

export const getPricesAndDiscounts = ({ item, isItemPrepaid }: { item: BreakdownItem; isItemPrepaid?: boolean }) => {
  if (!item) return {};
  if (isItemPrepaid) return { amount: 'Prepaid' };
  const skuBasePrice = item.skuAmountFormatted;
  const discountsTotal = getDiscountsTotal(item);
  const subTotalObj = {
    amount: currencyFormatter(2).format(item.amount / 100),
    lineThroughText: discountsTotal > 0 ? currencyFormatter(2).format((item.amount + discountsTotal) / 100) : null,
  };
  return { amount: skuBasePrice, subTotal: subTotalObj };
};

/**
 * Simplifies a breakdown item for display in the cart dropdown menu.
 *
 * Derived from the original `breakdownItemsSelector`, this function extracts
 * essential information from a breakdown item, formatting it for compact
 * display in the navigation. It handles price formatting, prepaid items,
 * and includes only necessary fields for the current implementation of
 * the cart preview.
 *
 * For additional functionality, refer to the original `breakdownItemsSelector` in
 * the hellotech-clients app.
 */
export const simplifyBreakdownItemForDisplay = (item: BreakdownItem): TSimplifiedBreakdownItem => {
  const { skuId, name, prepaid, image, quantity, priceType } = item;
  const isItemPrepaid = [Prepaid.Base, Prepaid.Full].includes(prepaid);
  const pricesAndDiscounts = getPricesAndDiscounts({ item, isItemPrepaid });
  return {
    skuId,
    name,
    quantity,
    subTotal: {
      amount: pricesAndDiscounts.amount,
      lineThroughText: pricesAndDiscounts.subTotal?.lineThroughText || null,
    },
    image,
    priceType,
  };
};
